import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { 
  ArrowLeft, Plus, Check, Clock, Sun, Sunset, Moon, Star,
  Calendar, ChevronLeft, ChevronRight, FileText, Trash2,
  MessageSquare, User, CheckCircle, Circle, MoreVertical,
  ListChecks, BarChart3, Settings2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const TIME_SLOTS = [
  { value: 'morning', label: 'Morning', icon: Sun, color: 'text-amber-500' },
  { value: 'afternoon', label: 'Afternoon', icon: Sunset, color: 'text-orange-500' },
  { value: 'evening', label: 'Evening', icon: Moon, color: 'text-indigo-500' },
  { value: 'anytime', label: 'Anytime', icon: Clock, color: 'text-slate-500' }
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-rose-100 text-rose-700' },
  { value: 'normal', label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  { value: 'low', label: 'Low', color: 'bg-slate-50 text-slate-500' }
];

export default function DailyChecklist() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('today');
  
  // Dialog states
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(null);
  
  // Form states
  const [newItem, setNewItem] = useState({ title: '', time_slot: 'anytime', priority: 'normal', notes: '' });
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', items: [] });
  const [newTemplateItem, setNewTemplateItem] = useState({ title: '', time_slot: 'morning', priority: 'normal' });
  const [itemNotes, setItemNotes] = useState('');

  const isFamily = user?.role === 'family_caregiver';

  useEffect(() => {
    fetchTodayChecklist();
    fetchTemplates();
    fetchSummary();
  }, []);

  const fetchTodayChecklist = async () => {
    try {
      const response = await api.get(`/daily-checklist/today`);
      setChecklist(response.data.checklist);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklistByDate = async (date) => {
    try {
      const response = await api.get(`/daily-checklist/date/${date}`);
      setChecklist(response.data.checklist);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get(`/daily-checklist/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get(`/daily-checklist/summary?days=7`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    try {
      await api.post(`/daily-checklist/items`, newItem);
      toast.success('Task added!');
      setNewItem({ title: '', time_slot: 'anytime', priority: 'normal', notes: '' });
      setAddItemOpen(false);
      fetchTodayChecklist();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleCompleteItem = async (itemId, completed) => {
    try {
      await api.put(`/daily-checklist/items/${itemId}/complete`, { completed });
      // Update local state immediately for better UX
      setChecklist(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.item_id === itemId 
            ? { ...item, completed, completed_at: completed ? new Date().toISOString() : null, completed_by_name: completed ? user.name : null }
            : item
        )
      }));
      fetchSummary();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleUpdateNotes = async (itemId) => {
    try {
      await api.put(`/daily-checklist/items/${itemId}/notes`, { notes: itemNotes });
      toast.success('Notes saved!');
      setNotesOpen(null);
      fetchTodayChecklist();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/daily-checklist/items/${itemId}`);
      toast.success('Task removed');
      fetchTodayChecklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (newTemplate.items.length === 0) {
      toast.error('Please add at least one task to the template');
      return;
    }
    try {
      await api.post(`/daily-checklist/templates`, newTemplate);
      toast.success('Template created!');
      setNewTemplate({ name: '', description: '', items: [] });
      setTemplateOpen(false);
      fetchTemplates();
      fetchTodayChecklist(); // Refresh to include new template items
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/daily-checklist/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const addItemToTemplate = () => {
    if (!newTemplateItem.title.trim()) return;
    setNewTemplate(prev => ({
      ...prev,
      items: [...prev.items, { ...newTemplateItem, order: prev.items.length }]
    }));
    setNewTemplateItem({ title: '', time_slot: 'morning', priority: 'normal' });
  };

  const removeItemFromTemplate = (index) => {
    setNewTemplate(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleDateChange = (direction) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + direction);
    const newDate = current.toISOString().split('T')[0];
    setSelectedDate(newDate);
    fetchChecklistByDate(newDate);
  };

  const getTimeSlotIcon = (slot) => {
    const ts = TIME_SLOTS.find(t => t.value === slot);
    if (!ts) return Clock;
    return ts.icon;
  };

  const getTimeSlotColor = (slot) => {
    const ts = TIME_SLOTS.find(t => t.value === slot);
    return ts?.color || 'text-slate-500';
  };

  const groupItemsByTimeSlot = (items) => {
    const groups = { morning: [], afternoon: [], evening: [], anytime: [] };
    items?.forEach(item => {
      const slot = item.time_slot || 'anytime';
      if (groups[slot]) groups[slot].push(item);
    });
    return groups;
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const groupedItems = groupItemsByTimeSlot(checklist?.items);
  const completedCount = checklist?.items?.filter(i => i.completed).length || 0;
  const totalCount = checklist?.items?.length || 0;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="daily-checklist">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Daily Care Checklist</h1>
              <p className="text-xs sm:text-sm text-slate-500">
                {checklist?.care_recipient_name ? `For ${checklist.care_recipient_name}` : 'Track daily care tasks'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isFamily && (
              <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="manage-templates-btn">
                    <Settings2 className="w-4 h-4" />
                    Templates
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Manage Templates</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    {/* Existing Templates */}
                    {templates.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Existing Templates</Label>
                        {templates.map(tmpl => (
                          <div key={tmpl.template_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium">{tmpl.name}</p>
                              <p className="text-xs text-slate-500">{tmpl.items?.length || 0} tasks</p>
                            </div>
                            <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => handleDeleteTemplate(tmpl.template_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Create New Template */}
                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium">Create New Template</Label>
                      <div className="space-y-3 mt-2">
                        <Input
                          placeholder="Template name (e.g., Morning Routine)"
                          value={newTemplate.name}
                          onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                          data-testid="template-name-input"
                        />
                        <Textarea
                          placeholder="Description (optional)"
                          value={newTemplate.description}
                          onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                          className="min-h-[60px]"
                        />
                        
                        {/* Add items to template */}
                        <div className="bg-slate-50 p-3 rounded-lg space-y-2">
                          <Label className="text-xs">Add tasks to template</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Task title"
                              value={newTemplateItem.title}
                              onChange={(e) => setNewTemplateItem({ ...newTemplateItem, title: e.target.value })}
                              className="flex-1"
                              data-testid="template-item-title"
                            />
                            <Select value={newTemplateItem.time_slot} onValueChange={(v) => setNewTemplateItem({ ...newTemplateItem, time_slot: v })}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIME_SLOTS.map(ts => (
                                  <SelectItem key={ts.value} value={ts.value}>{ts.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="sm" onClick={addItemToTemplate} data-testid="add-template-item-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Template items list */}
                          {newTemplate.items.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {newTemplate.items.map((item, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                                  <span>{item.title}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="text-xs">{item.time_slot}</Badge>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeItemFromTemplate(i)}>
                                      <Trash2 className="w-3 h-3 text-slate-400" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <Button onClick={handleCreateTemplate} className="w-full bg-teal-500 hover:bg-teal-600" data-testid="save-template-btn">
                          Save Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="add-task-btn">
                  <Plus className="w-4 h-4" />
                  Add Task
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Task to Today's Checklist</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Task Title</Label>
                    <Input
                      placeholder="e.g., Give morning medication"
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      className="mt-1"
                      data-testid="task-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Time Slot</Label>
                      <Select value={newItem.time_slot} onValueChange={(v) => setNewItem({ ...newItem, time_slot: v })}>
                        <SelectTrigger className="mt-1" data-testid="time-slot-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map(ts => (
                            <SelectItem key={ts.value} value={ts.value}>
                              <div className="flex items-center gap-2">
                                <ts.icon className={`w-4 h-4 ${ts.color}`} />
                                {ts.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={newItem.priority} onValueChange={(v) => setNewItem({ ...newItem, priority: v })}>
                        <SelectTrigger className="mt-1" data-testid="priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      placeholder="Additional details..."
                      value={newItem.notes}
                      onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <Button onClick={handleAddItem} className="w-full bg-teal-500 hover:bg-teal-600" data-testid="save-task-btn">
                    Add Task
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full mb-4 sm:mb-6">
            <TabsTrigger value="today" className="flex items-center gap-2">
              <ListChecks className="w-4 h-4" />
              Today
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="summary" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Summary
            </TabsTrigger>
          </TabsList>

          {/* Today's Checklist */}
          <TabsContent value="today" className="space-y-6">
            {/* Progress Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Today's Progress</p>
                    <p className="text-2xl font-bold text-slate-900">{completedCount} / {totalCount}</p>
                  </div>
                  <div className="w-20 h-20 relative">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="40" cy="40" r="35" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                      <circle 
                        cx="40" cy="40" r="35" fill="none" stroke="#14b8a6" strokeWidth="6"
                        strokeDasharray={`${completionRate * 2.2} 220`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-teal-600">
                      {completionRate}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task Lists by Time Slot */}
            {totalCount === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <ListChecks className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks for today</h3>
                  <p className="text-slate-500 mb-4">
                    {templates.length === 0 
                      ? "Create a template to automatically populate daily tasks, or add individual tasks."
                      : "Add a task to get started with today's checklist."}
                  </p>
                  <Button onClick={() => setAddItemOpen(true)} className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4" />
                    Add First Task
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {TIME_SLOTS.map(slot => {
                  const items = groupedItems[slot.value] || [];
                  if (items.length === 0) return null;
                  
                  const SlotIcon = slot.icon;
                  const slotCompleted = items.filter(i => i.completed).length;
                  
                  return (
                    <Card key={slot.value}>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center justify-between text-base">
                          <div className="flex items-center gap-2">
                            <SlotIcon className={`w-5 h-5 ${slot.color}`} />
                            {slot.label}
                          </div>
                          <Badge variant="secondary">{slotCompleted}/{items.length}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {items.map(item => {
                          const priorityStyle = PRIORITIES.find(p => p.value === item.priority);
                          return (
                            <div 
                              key={item.item_id}
                              className={`flex items-start gap-3 p-3 rounded-xl transition-all ${
                                item.completed ? 'bg-slate-50' : 'bg-white border border-slate-200'
                              }`}
                            >
                              <button
                                onClick={() => handleCompleteItem(item.item_id, !item.completed)}
                                className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                  item.completed 
                                    ? 'bg-teal-500 border-teal-500 text-white' 
                                    : 'border-slate-300 hover:border-teal-500'
                                }`}
                                data-testid={`complete-${item.item_id}`}
                              >
                                {item.completed && <Check className="w-4 h-4" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium ${item.completed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                                    {item.title}
                                  </p>
                                  {item.priority === 'high' && (
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                  )}
                                </div>
                                {item.completed && item.completed_at && (
                                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    {item.completed_by_name} at {new Date(item.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                                {item.notes && (
                                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" />
                                    {item.notes}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => { setNotesOpen(item.item_id); setItemNotes(item.notes || ''); }}
                                >
                                  <MessageSquare className="w-4 h-4 text-slate-400" />
                                </Button>
                                {!item.template_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-rose-400 hover:text-rose-600"
                                    onClick={() => handleDeleteItem(item.item_id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            {/* Date Navigation */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => handleDateChange(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="text-center">
                    <p className="font-medium text-slate-900">
                      {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                    {isToday && <Badge className="bg-teal-100 text-teal-700 mt-1">Today</Badge>}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleDateChange(1)} disabled={isToday}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Show checklist for selected date - reuse same display logic */}
            {checklist && checklist.date === selectedDate ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    Tasks
                    <Badge variant="secondary">
                      {checklist.items?.filter(i => i.completed).length || 0}/{checklist.items?.length || 0}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {checklist.items?.length > 0 ? (
                    checklist.items.map(item => (
                      <div key={item.item_id} className={`flex items-center gap-3 p-3 rounded-lg ${item.completed ? 'bg-teal-50' : 'bg-slate-50'}`}>
                        {item.completed ? (
                          <CheckCircle className="w-5 h-5 text-teal-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300" />
                        )}
                        <div className="flex-1">
                          <p className={item.completed ? 'text-slate-600' : 'text-slate-900'}>{item.title}</p>
                          {item.completed_at && (
                            <p className="text-xs text-slate-400">
                              Completed by {item.completed_by_name} at {new Date(item.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">No checklist for this date</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No checklist found for this date</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            {summary ? (
              <>
                {/* Overall Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">7-Day Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-teal-600">{summary.overall?.completion_rate || 0}%</p>
                        <p className="text-sm text-slate-500">Completion Rate</p>
                      </div>
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900">{summary.overall?.completed_items || 0}</p>
                        <p className="text-sm text-slate-500">Tasks Done</p>
                      </div>
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900">{summary.overall?.days_tracked || 0}</p>
                        <p className="text-sm text-slate-500">Days Tracked</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Daily Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {summary.summary?.length > 0 ? (
                      summary.summary.map(day => (
                        <div key={day.date} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">
                              {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </p>
                            <p className="text-sm text-slate-500">{day.completed_items}/{day.total_items} tasks</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-teal-500 rounded-full transition-all"
                                style={{ width: `${day.completion_rate}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-700 w-12 text-right">{day.completion_rate}%</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">No data available yet</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Start tracking tasks to see your summary</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Notes Dialog */}
      <Dialog open={!!notesOpen} onOpenChange={() => setNotesOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Task Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Textarea
              placeholder="Add notes about this task..."
              value={itemNotes}
              onChange={(e) => setItemNotes(e.target.value)}
              className="min-h-[100px]"
              data-testid="task-notes-input"
            />
            <Button onClick={() => handleUpdateNotes(notesOpen)} className="w-full bg-teal-500 hover:bg-teal-600" data-testid="save-notes-btn">
              Save Notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
